import * as React from 'react';
import {View, type ViewProps} from '@packages/systems/spring';
import {useFluxContext} from '@packages/systems/designer/flux';
import {canvasViewportChangeStarted} from '@packages/systems/open-canvas/store';

const MIDDLE_BUTTON = 1;

export type PanActivationHandlers = {
  /**
   * Attach to the React `onPointerDown` prop of any surface that should
   * start a middle-mouse pan. Dispatches
   * `canvasViewportChangeStarted({trigger: 'middle-mouse-pan-event',
   * isTriggeredFromIframe: false, analytics: {actionType: 'pan'}})` --
   * the reducer adds `'middle-mouse-pan-event'` to
   * `PanModeStore.activeSources` -- and calls `preventDefault` so the
   * browser doesn't enter middle-button autoscroll mode.
   *
   * Subsequent drag tracking is owned by `PanSurface`, which mounts as
   * a side effect of pan mode becoming active. `PanSurface` also
   * handles the release path: in-component `pointerup` for the normal
   * in-viewport case, and a window-level `mouseup` listener for
   * click-without-move (no `pointermove` ever fires before the
   * release).
   */
  onPointerDown: (event: React.PointerEvent) => void;
  /**
   * Suppresses the browser's middle-click defaults (autoscroll cursor,
   * paste-link on Linux) for any surface that opts into pan activation.
   */
  onAuxClick: (event: React.MouseEvent) => void;
};

type PanActivationZoneProps = ViewProps;

export const PanActivationZone = React.forwardRef<
  HTMLDivElement,
  PanActivationZoneProps
>((props, ref) => {
  const flux = useFluxContext();

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== MIDDLE_BUTTON) return;
      event.preventDefault();

      flux.dispatch(
        canvasViewportChangeStarted({
          trigger: 'middle-mouse-pan-event',
          isTriggeredFromIframe: false,
          analytics: {actionType: 'pan'},
        })
      );
    },
    [flux]
  );

  const onAuxClick = React.useCallback((event: React.MouseEvent) => {
    if (event.button !== MIDDLE_BUTTON) return;
    event.preventDefault();
  }, []);

  // Must stay stacking-transparent: `FrameUI.Frame` wraps each `FrameView` in
  // one of these, and the frame stacking tiers in `FrameView.tsx` compare
  // `z-index` across sibling frames. A `position`, `transform`, `opacity`,
  // `filter`, or `isolation` here would give every `FrameView` its own
  // stacking context and break that ordering silently (CANV-2375).
  return (
    <View
      {...props}
      ref={ref}
      onPointerDown={onPointerDown}
      onAuxClick={onAuxClick}
    />
  );
});
